import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Plus, Trash2, Edit2, Save, X, Loader2, Award } from 'lucide-react';
import { toast } from 'sonner';

const CMSCertifications = () => {
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo: ''
  });

  useEffect(() => {
    fetchCertifications();
  }, []);

  const fetchCertifications = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${BACKEND_URL}/api/admin/certifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setCertifications(await response.json());
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (cert) => {
    setEditingId(cert.id);
    setFormData({
      name: cert.name || '',
      description: cert.description || '',
      logo: cert.logo || ''
    });
    setShowForm(true);
  };

  const handleNew = () => {
    setEditingId(null);
    setFormData({
      name: '',
      description: '',
      logo: ''
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Le nom est requis');
      return;
    }

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('admin_token');
      
      const url = editingId 
        ? `${BACKEND_URL}/api/admin/certifications/${editingId}`
        : `${BACKEND_URL}/api/admin/certifications`;
      
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success(editingId ? 'Certification mise à jour !' : 'Certification créée !');
        handleCancel();
        fetchCertifications();
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette certification ?')) return;
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${BACKEND_URL}/api/admin/certifications/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Certification supprimée');
        fetchCertifications();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          <Loader2 size={32} className="animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ maxWidth: '900px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '8px' }}>
              Certifications
            </h1>
            <p style={{ color: '#666' }}>Gérez vos certifications et labels qualité</p>
          </div>
          <button
            onClick={handleNew}
            style={{
              padding: '12px 24px',
              background: 'var(--brand-primary)',
              color: 'var(--brand-dark)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Plus size={18} /> Nouvelle certification
          </button>
        </div>

        {/* Formulaire */}
        {showForm && (
          <div style={{ 
            background: 'white', 
            borderRadius: '12px', 
            padding: '24px', 
            marginBottom: '24px',
            border: '2px solid var(--brand-primary)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600' }}>
                {editingId ? 'Modifier la certification' : 'Nouvelle certification'}
              </h3>
              <button onClick={handleCancel} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Nom *</label>
              <input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px' }}
                placeholder="RGE, QualiPV, etc."
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px', resize: 'vertical' }}
                placeholder="Reconnu Garant de l'Environnement..."
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>URL du logo</label>
              <input
                value={formData.logo}
                onChange={(e) => setFormData(prev => ({ ...prev, logo: e.target.value }))}
                style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px' }}
                placeholder="https://..."
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleSave}
                style={{
                  padding: '12px 24px',
                  background: 'var(--brand-primary)',
                  color: 'var(--brand-dark)',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Save size={18} /> Sauvegarder
              </button>
              <button
                onClick={handleCancel}
                style={{
                  padding: '12px 24px',
                  background: '#f0f0f0',
                  color: '#333',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        )}

        {/* Liste des certifications */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
          {certifications.map(cert => (
            <div 
              key={cert.id} 
              style={{ 
                background: 'white', 
                borderRadius: '12px', 
                padding: '20px',
                border: '1px solid #eee'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'start', gap: '16px' }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  background: 'var(--brand-primary)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {cert.logo ? (
                    <img 
                      src={cert.logo} 
                      alt={cert.name}
                      style={{ width: '36px', height: '36px', objectFit: 'contain' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<span style="font-size:24px">🏆</span>';
                      }}
                    />
                  ) : (
                    <Award size={24} style={{ color: 'var(--brand-dark)' }} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>
                    {cert.name}
                  </h3>
                  {cert.description && (
                    <p style={{ fontSize: '13px', color: '#666', lineHeight: '1.4' }}>
                      {cert.description}
                    </p>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button
                  onClick={() => handleEdit(cert)}
                  style={{
                    flex: 1,
                    padding: '8px',
                    background: 'var(--brand-dark)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    fontSize: '13px'
                  }}
                >
                  <Edit2 size={14} /> Modifier
                </button>
                <button
                  onClick={() => handleDelete(cert.id)}
                  style={{
                    padding: '8px 12px',
                    background: '#ff4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {certifications.length === 0 && !showForm && (
          <div style={{ 
            background: 'white', 
            borderRadius: '12px', 
            padding: '48px', 
            textAlign: 'center' 
          }}>
            <Award size={48} style={{ color: '#ddd', marginBottom: '16px' }} />
            <p style={{ color: '#666', marginBottom: '16px' }}>Aucune certification pour le moment</p>
            <button
              onClick={handleNew}
              style={{
                padding: '12px 24px',
                background: 'var(--brand-primary)',
                color: 'var(--brand-dark)',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Ajouter votre première certification
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default CMSCertifications;
